import { createReducer } from '@reduxjs/toolkit';

import { Nullable } from 'modules/types/common';

import * as types from 'store/booking/types';
import * as actions from 'store/booking/actions';
// import { ProviderSummary } from 'store/providerSearch/types';
import { ProviderSummary } from 'store/findProvider/types';

export type AppointmentDetailsState = Readonly<{
  isFetching: boolean;
  error: Nullable<Error>;
  id: Nullable<string>;
  patient: Nullable<types.Patient>;
  doctor: Nullable<ProviderSummary>;
  specialty: Nullable<types.Specialty>;
  groupedVisitType: Nullable<types.GroupedVisitType>;
  visitType: Nullable<types.VisitType>;
  appointmentType: Nullable<types.AppointmentType>;
  appointmentLocation: Nullable<types.AppointmentLocation>;
  appointmentDueDate: string;
  appointmentDate: string;
  appointmentTime: Nullable<types.TimeSlot>;
  isLockingAppointment: boolean;
  lockedAppointment: Nullable<types.LockedAppointment>;
  reasonForVisit: string;
  isEditingAppointment: boolean;
  fetchSlots: boolean;
  updatedVisitGroup: boolean;
  isExternalRequest: boolean;
}>;

export const appointmentDetailsInitialState: AppointmentDetailsState = {
  isFetching: false,
  error: null,
  id: null,
  patient: null,
  doctor: null,
  specialty: null,
  groupedVisitType: null,
  visitType: null,
  appointmentType: null,
  appointmentLocation: null,
  appointmentDate: '',
  appointmentDueDate: '',
  appointmentTime: null,
  isLockingAppointment: false,
  lockedAppointment: null,
  reasonForVisit: '',
  isEditingAppointment: false,
  fetchSlots: false,
  updatedVisitGroup: false,
  isExternalRequest: false
};

export const appointmentDetailsReducer = createReducer(appointmentDetailsInitialState, {
  [actions.SET_PATIENT]: (state, action) => {
    state.patient = action.payload;
  },
  [actions.SET_SPECIALTY]: (state, action) => {
    state.specialty = action.payload;
  },
  [actions.SET_DOCTOR]: (state, action) => {
    state.doctor = action.payload;
  },
  [actions.SET_GROUPED_VISIT_TYPE]: (state, action) => {
    state.groupedVisitType = action.payload;
  },
  [actions.SET_APPOINTMENT_TYPE]: (state, action) => {
    state.appointmentType = action.payload;
  },
  [actions.SET_APPOINTMENT_LOCATION]: (state, action) => {
    state.appointmentLocation = action.payload;
    state.appointmentDate = '';
  },
  [actions.SET_APPOINTMENT_DUE_DATE]: (state, action) => {
    state.appointmentDueDate = action.payload;
  },
  [actions.SET_APPOINTMENT_DATE]: (state, action) => {
    state.appointmentDate = action.payload;
  },
  [actions.SET_APPOINTMENT_TIME]: (state, action) => {
    state.appointmentTime = action.payload;
  },
  [actions.SET_FETCH_SLOTS]: (state, action) => {
    state.fetchSlots = action.payload;
  },
  [actions.SET_UPDATED_VISIT_GROUP]: (state, action) => {
    state.updatedVisitGroup = action.payload;
  },
  [actions.RESET_APPOINTMENT_TIME]: state => {
    state.appointmentTime = null;
  },
  [actions.SET_REASON_FOR_VISIT]: (state, action) => {
    state.reasonForVisit = action.payload;
  },
  [actions.LOCK_TIME_SLOT]: state => {
    state.isLockingAppointment = true;
    state.error = null;
  },
  [actions.LOCK_TIME_SLOT_FAIL]: (state, action) => {
    state.isLockingAppointment = false;
    state.error = action.error;
  },
  [actions.LOCK_TIME_SLOT_SUCCESS]: (state, action) => {
    state.isLockingAppointment = false;
    state.lockedAppointment = action.payload.data;
  },
  [actions.MODIFY_APPOINTMENT]: (state, action) => {
    state.id = action.payload;
    state.isEditingAppointment = true;
  },
  [actions.MODIFY_APPOINTMENT_FAIL]: (state, action) => {
    state.isEditingAppointment = false;
    state.error = action.error;
  },
  [actions.SET_IS_EXTERNAL_REQUEST]: (state, action) => {
    state.isExternalRequest = action.payload;
  },
  [actions.MODIFY_APPOINTMENT_SUCCESS]: (state, action) => {
    const { patient, doctor, visitType, appointmentLocation, groupedVisitType } = action.payload;

    state.patient = patient;
    state.doctor = doctor;
    state.visitType = visitType;
    state.appointmentLocation = appointmentLocation;
    state.groupedVisitType = groupedVisitType;
    state.appointmentDate = '';
    state.appointmentTime = null;
  },
  [actions.START_BOOKING_FROM_COST_ESTIMATOR]: (state, action) => {
    state.appointmentTime = action.payload;
  },
  [actions.CLEAR_APPOINTMENT]: () => appointmentDetailsInitialState
});

export default appointmentDetailsReducer;
